import React, { useId, useState } from 'react';
import { FaChevronDown, FaMotorcycle, FaStore } from 'react-icons/fa';
import CartLineAddOns from '../../../components/CartLineAddOns';
import {
  formatCartLineTitle,
  getCartLineHostUnitPrice,
  getCartLineTotal,
  normalizeCartLineAddOns,
  resolveCartLineKey,
} from '../../OurMenu/cartAddOnUtils';

/**
 * @param {{
 *   cartItems: Array<{ id: number, name: string, price: number, quantity: number, image?: string, category?: string, addOns?: Array<{ id?: number, name: string, price: number, quantity?: number }> }>,
 *   fulfillmentType: 'delivery' | 'pickup',
 *   appliedCoupon: { code: string } | null,
 *   charges: {
 *     cartTotal: number,
 *     discount: number,
 *     packagingFee: number,
 *     delivery: number,
 *     gstTax: number,
 *     grandTotal: number,
 *   },
 *   contactLine?: string,
 *   loading?: boolean,
 *   variant?: 'summary' | 'collapsible',
 *   isResumeMode?: boolean,
 * }} props
 */
const PaymentBillCollapsible = ({
  cartItems,
  fulfillmentType,
  appliedCoupon,
  charges,
  contactLine,
  loading,
}) => {
  const { cartTotal, discount, packagingFee, delivery, gstTax, grandTotal } = charges;
  const [billOpen, setBillOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const billPanelId = useId();
  const itemsPanelId = useId();
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <section className="payment-bill payment-bill--collapsible" aria-label="Bill details">
      <button
        type="button"
        className={`payment-bill__toggle${billOpen ? ' payment-bill__toggle--open' : ''}`}
        aria-expanded={billOpen}
        aria-controls={billPanelId}
        onClick={() => setBillOpen((v) => !v)}
      >
        <span>View bill details</span>
        <span className="payment-bill__toggle-amount">
          {loading ? '…' : `₹${grandTotal.toFixed(2)}`}
        </span>
        <FaChevronDown className="payment-bill__toggle-chevron" aria-hidden />
      </button>

      {billOpen ? (
        <div id={billPanelId} className="payment-bill__panel">
          {contactLine ? (
            <p className="payment-bill__contact">
              <span className="payment-bill__contact-label">Contact</span>
              {contactLine}
            </p>
          ) : null}

          <button
            type="button"
            className={`payment-bill__items-toggle${itemsOpen ? ' payment-bill__items-toggle--open' : ''}`}
            aria-expanded={itemsOpen}
            aria-controls={itemsPanelId}
            onClick={() => setItemsOpen((v) => !v)}
          >
            View items ({itemCount})
            <FaChevronDown aria-hidden />
          </button>

          {itemsOpen ? (
            <ul id={itemsPanelId} className="payment-bill__items">
              {cartItems.map((item) => (
                <li key={resolveCartLineKey(item)} className="payment-bill__item">
                  <div className="payment-bill__item-main">
                    <span className="payment-bill__item-name">
                      {formatCartLineTitle(item)}
                      <span className="payment-bill__item-qty"> × {item.quantity}</span>
                    </span>
                    <CartLineAddOns
                      variant="formula"
                      hostName={formatCartLineTitle(item)}
                      hostQty={item.quantity}
                      hostUnitPrice={getCartLineHostUnitPrice(item)}
                      addOns={item.addOns}
                      className="cart-line-addons--checkout"
                    />
                  </div>
                  <span className="payment-bill__item-price">
                    ₹{getCartLineTotal(item).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          <dl className="payment-bill__rows">
            <div className="payment-bill__row">
              <dt>Order type</dt>
              <dd className="payment-bill__order-type">
                {fulfillmentType === 'pickup' ? <FaStore aria-hidden /> : <FaMotorcycle aria-hidden />}
                {fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}
              </dd>
            </div>
            <div className="payment-bill__row">
              <dt>Subtotal</dt>
              <dd>{loading ? '…' : `₹${cartTotal.toFixed(2)}`}</dd>
            </div>
            {discount > 0 && appliedCoupon ? (
              <div className="payment-bill__row payment-bill__row--discount">
                <dt>Discount ({appliedCoupon.code})</dt>
                <dd>{loading ? '…' : `−₹${discount.toFixed(2)}`}</dd>
              </div>
            ) : null}
            <div className="payment-bill__row">
              <dt>Packaging</dt>
              <dd>{loading ? '…' : `₹${packagingFee.toFixed(2)}`}</dd>
            </div>
            {fulfillmentType !== 'pickup' ? (
              <div className="payment-bill__row">
                <dt>Delivery</dt>
                <dd>
                  {loading ? (
                    '…'
                  ) : delivery === 0 ? (
                    <span className="payment-bill__free">FREE</span>
                  ) : (
                    `₹${delivery.toFixed(2)}`
                  )}
                </dd>
              </div>
            ) : null}
            <div className="payment-bill__row">
              <dt>GST / Tax</dt>
              <dd>{loading ? '…' : `₹${gstTax.toFixed(2)}`}</dd>
            </div>
            <div className="payment-bill__row payment-bill__row--total">
              <dt>To pay</dt>
              <dd>{loading ? '…' : `₹${grandTotal.toFixed(2)}`}</dd>
            </div>
          </dl>
        </div>
      ) : null}
    </section>
  );
};

const PaymentBillItemsList = ({ cartItems, className = 'payment-summary__items' }) => {
  if (!cartItems?.length) return null;

  return (
    <ul className={`checkout-order-items ${className}`}>
      {cartItems.map((item) => (
        <li key={resolveCartLineKey(item)} className="checkout-order-item">
          <div className="co-item__img">
            {item.image ? (
              <img src={item.image} alt="" />
            ) : (
              <span className="co-item__placeholder" aria-hidden />
            )}
          </div>
          <div className="co-item__body">
            <div className="co-item__top">
              <div className="co-item__heading">
                <p className="co-item__name">{formatCartLineTitle(item)}</p>
                {normalizeCartLineAddOns(item.addOns).length === 0 &&
                item.quantity > 1 ? (
                  <p className="co-item__meta">Qty {item.quantity}</p>
                ) : null}
              </div>
              <span className="co-item__total">₹{getCartLineTotal(item).toFixed(2)}</span>
            </div>
            <CartLineAddOns
              variant="formula"
              hostName={formatCartLineTitle(item)}
              hostQty={item.quantity}
              hostUnitPrice={getCartLineHostUnitPrice(item)}
              addOns={item.addOns}
              className="cart-line-addons--checkout"
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const PaymentBillSummary = ({
  cartItems,
  fulfillmentType,
  appliedCoupon,
  charges,
  contactLine,
  loading,
}) => {
  const { cartTotal, discount, packagingFee, delivery, gstTax, grandTotal } = charges;

  return (
      <aside
        className="payment-summary checkout-summary"
        aria-labelledby="payment-summary-title"
      >
        <h2 id="payment-summary-title" className="checkout-section-title">
          Bill details
        </h2>

        {contactLine ? (
          <p className="payment-summary__contact">
            <span className="payment-summary__contact-label">Contact</span>
            {contactLine}
          </p>
        ) : null}

        <PaymentBillItemsList cartItems={cartItems} />

        <div className="checkout-price-rows payment-summary__rows">
          <div className="cpr cpr--fulfillment">
            <span>Order type</span>
            <span className="cpr-order-type">
              {fulfillmentType === 'pickup' ? <FaStore aria-hidden /> : <FaMotorcycle aria-hidden />}
              {fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}
            </span>
          </div>
          <div className="cpr">
            <span>Subtotal</span>
            <span>₹{cartTotal.toFixed(2)}</span>
          </div>
          {discount > 0 && appliedCoupon ? (
            <div className="cpr cpr--discount">
              <span>Discount ({appliedCoupon.code})</span>
              <span>−₹{discount.toFixed(2)}</span>
            </div>
          ) : null}
          <div className="cpr">
            <span>Packaging fee</span>
            <span>{loading ? '…' : `₹${packagingFee.toFixed(2)}`}</span>
          </div>
          {fulfillmentType !== 'pickup' ? (
            <div className="cpr">
              <span>Delivery</span>
              <span>
                {loading ? (
                  '…'
                ) : delivery === 0 ? (
                  <span className="cpr--free">FREE</span>
                ) : (
                  `₹${delivery.toFixed(2)}`
                )}
              </span>
            </div>
          ) : null}
          <div className="cpr">
            <span>GST / Tax</span>
            <span>{loading ? '…' : `₹${gstTax.toFixed(2)}`}</span>
          </div>
          <div className="cpr cpr--total">
            <span>To pay</span>
            <span>{loading ? '…' : `₹${grandTotal.toFixed(2)}`}</span>
          </div>
        </div>
      </aside>
    );
};

const PaymentBillDetails = (props) => {
  const { variant = 'collapsible', ...rest } = props;
  if (variant === 'summary') {
    return <PaymentBillSummary {...rest} />;
  }
  return <PaymentBillCollapsible {...rest} />;
};

export default PaymentBillDetails;
